/* eslint-disable @typescript-eslint/no-explicit-any */
import { get } from "lodash";
import { ReactNode, useCallback } from "react";

import { useDynamicGridTranslate, usePageContext } from "@ryde/redux";
import {
  DEFAULT_COLUMN_WIDTH,
  fieldNameMetadataResolve,
  getComputedGridFieldName,
  getGridFieldColumnWidthById,
  IColumnDef,
  IColumnDefMeta,
  ICustomField,
  IExtendableDto,
  IRydeCellContext,
  IRydeColumnHelper,
  parseBoolean,
} from "@ryde/shared";

import { RenderCellTemplate } from "../../../../dynamic-forms";
import { GenericSortableCell } from "../../cells";

export const useTableColumnBuilders = <T extends IExtendableDto>() => {
  const { getGridLabelComputed, getGridLabelTranslationKeyComputed } =
    useDynamicGridTranslate();
  const { entity } = usePageContext();

  const getColumnMetadata = useCallback(
    ({
      control,
      columnWidth,
    }: {
      control: ICustomField;
      columnWidth: string | number;
    }): IColumnDefMeta => {
      const { id, gridOrderable } = control;
      return {
        customFieldId: id !== undefined ? Number(id) : undefined,
        orderable: parseBoolean(gridOrderable),
        displayName: getGridLabelComputed(entity, control),
        translationKey: getGridLabelTranslationKeyComputed(entity, control),
        columnWidth,
      };
    },
    [entity, getGridLabelComputed, getGridLabelTranslationKeyComputed],
  );

  const getCellTemplate = useCallback(
    ({
      control,
      info,
    }: {
      control: ICustomField;
      info: IRydeCellContext<T, unknown>;
    }) => {
      const { id, gridAttributes } = control;
      return (
        <RenderCellTemplate
          key={`${id}_${info.row.original?.id || 0}`}
          truncateChars={gridAttributes?.gridTruncateChars}
          {...control}
          value={info.getValue() as ReactNode}
          rowId={info.row.original?.id || 0}
          rowOriginal={info.row.original}
        />
      );
    },
    [],
  );

  const getHeaderCell = useCallback(
    ({
      control,
      fieldNameResolved,
    }: {
      control: ICustomField;
      fieldNameResolved: string;
    }) => {
      const gridHeaderText = getGridLabelComputed(entity, control);
      const gridHeaderTranslationKey = getGridLabelTranslationKeyComputed(
        entity,
        control,
      );
      return (
        <GenericSortableCell
          fieldNameResolved={fieldNameResolved}
          groupingDisabled={!control.gridGroupable}
          sortingDisabled={!control.gridSortable}
          translationKey={gridHeaderTranslationKey}
        >
          {gridHeaderText}
        </GenericSortableCell>
      );
    },
    [entity, getGridLabelComputed, getGridLabelTranslationKeyComputed],
  );

  const buildColumnAccessor = useCallback(
    ({
      control,
      genericColumnHelper,
    }: {
      control: ICustomField;
      genericColumnHelper: IRydeColumnHelper<T>;
    }): IColumnDef<T, any> => {
      const {
        name: fieldNamePure,
        gridAttributes,
        gridFieldTypeId,
        isColumn,
      } = control;
      const { fieldNameGrid, gridColumnWidth: gridColumnWidthApiValue } =
        gridAttributes || {};

      const mappedWidth = getGridFieldColumnWidthById(gridFieldTypeId);
      const fieldNameResolved =
        fieldNameGrid || fieldNameMetadataResolve(fieldNamePure, isColumn);
      const columnWidth =
        gridColumnWidthApiValue || mappedWidth || DEFAULT_COLUMN_WIDTH;

      const colAccessor: IColumnDef<T, any> = {
        id: getComputedGridFieldName(control),
        header: () => getHeaderCell({ control, fieldNameResolved }),
        cell: info => getCellTemplate({ control, info }),
        meta: getColumnMetadata({
          control,
          columnWidth,
        }),
      };

      return genericColumnHelper.accessor((x: any) => {
        return get(x, fieldNameResolved);
      }, colAccessor);
    },
    [getColumnMetadata, getCellTemplate, getHeaderCell],
  );

  return {
    buildColumnAccessor,
  };
};
